import { useMemo } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import type { POI } from '@lib/types';

interface KhoudMapProps {
  /** Map center as [lat, lng]. Falls back to first POI if not provided. */
  center: [number, number];
  /** POIs to render. Filter upstream (e.g. only anchors). */
  pois: POI[];
  /** Initial zoom level. Default 13 covers Khoud + airport. */
  zoom?: number;
  /** Tailwind classes for the wrapper. Pass height utilities here (h-[280px] md:h-[400px]). */
  className?: string;
  /** Hex of the highlighted marker (KEY2 itself). Defaults to brand gold. */
  highlightColor?: string;
  /** Hex for all other markers. Defaults to brand navy. */
  baseColor?: string;
  /** Optional ARIA label for the map region. */
  ariaLabel?: string;
}

/**
 * Reusable map for KEY2's Al Khoud surroundings.
 * Uses divIcon (no PNG assets) to avoid Vite's leaflet default-marker resolution issue.
 * Pin styled gold for category='project', navy for everything else.
 */
export default function KhoudMap({
  center,
  pois,
  zoom = 13,
  className = 'h-[280px] md:h-[400px] w-full rounded-xl overflow-hidden border border-navy-100',
  highlightColor = '#C9A84C',
  baseColor = '#0A1628',
  ariaLabel,
}: KhoudMapProps) {
  const { highlightIcon, baseIcon } = useMemo(
    () => ({
      highlightIcon: makePinIcon(highlightColor, true),
      baseIcon: makePinIcon(baseColor, false),
    }),
    [highlightColor, baseColor]
  );

  return (
    <div className={className} aria-label={ariaLabel} role="region">
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom={false}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {pois.map((poi) => {
          const isProject = poi.category === 'project';
          return (
            <Marker
              key={`${poi.lat}-${poi.lng}-${poi.name_en}`}
              position={[poi.lat, poi.lng]}
              icon={isProject ? highlightIcon : baseIcon}
            >
              <Popup>
                <div dir="rtl" className="text-right text-sm">
                  <div className="font-semibold text-navy">{poi.name_ar}</div>
                  {typeof poi.distance_min === 'number' && (
                    <div className="text-xs text-navy-600 mt-0.5">
                      <span className="num-en">{poi.distance_min}</span> دقيقة
                    </div>
                  )}
                  {poi.demand && (
                    <div className="text-xs text-navy-500 mt-1">{poi.demand}</div>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}

function makePinIcon(color: string, large: boolean): L.DivIcon {
  const size = large ? 30 : 22;
  const ring = large ? 4 : 3;
  const html = `
    <div style="
      width: ${size}px;
      height: ${size}px;
      border-radius: 50% 50% 50% 0;
      background: ${color};
      border: ${ring}px solid #ffffff;
      box-shadow: 0 2px 6px rgba(10,22,40,0.35);
      transform: rotate(-45deg);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        width: ${size / 3}px;
        height: ${size / 3}px;
        background: #ffffff;
        border-radius: 50%;
        transform: rotate(45deg);
      "></div>
    </div>
  `;
  return L.divIcon({
    html,
    className: 'key2-map-pin',
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
}
